'use client';

import { produce } from 'immer';
import { create } from 'zustand';

import { materialsApi, type UserMaterial } from '@/lib/api/materials';
import { getUserFacingErrorMessage } from '@/lib/errors/user-facing-error';
import { appendExploreImageReferenceToken, remapExploreImageReferenceTokens } from '@/lib/explore-image-reference-tokens';
import { getPreferredImageUrl } from '@/lib/image-utils';
import { translateClient } from '@/lib/i18n/client';
import type { AttachedNodeRef } from '@/types/chat';

export interface ExploreVideoComposerBoundImage {
  token: string;
  source: 'upload';
  previewUrl: string;
  attachment: AttachedNodeRef;
}

export type ExploreVideoComposerUploadResult =
  | { ok: true }
  | { ok: false; code: 'upload-failed'; message?: string };

interface ExploreVideoComposerState {
  boundImages: ExploreVideoComposerBoundImage[];
  draft: string;
  isUploading: boolean;
  error: string | null;
  openEmpty: () => void;
  setDraft: (draft: string) => void;
  uploadReferenceImages: (
    files: File[],
    capabilitySlotKeys?: Array<AttachedNodeRef['capabilitySlotKey']>,
  ) => Promise<ExploreVideoComposerUploadResult>;
  removeBoundImage: (token: string) => void;
  clearBoundImages: () => void;
  insertBoundImageReference: (token: string) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = () => ({
  boundImages: [] as ExploreVideoComposerBoundImage[],
  draft: '',
  isUploading: false,
  error: null as string | null,
});

function buildToken(index: number): string {
  return `image${index}`;
}

function reindex(images: ExploreVideoComposerBoundImage[]): ExploreVideoComposerBoundImage[] {
  return images.map((image, index) => {
    const token = buildToken(index + 1);
    return { ...image, token, attachment: { ...image.attachment, label: token } };
  });
}

function tokenRemap(
  previous: ExploreVideoComposerBoundImage[],
  remaining: ExploreVideoComposerBoundImage[],
  reindexed: ExploreVideoComposerBoundImage[],
): Record<string, string | null> {
  return Object.fromEntries(previous.map((image) => {
    const nextIndex = remaining.findIndex((candidate) => candidate.token === image.token);
    return [image.token, nextIndex >= 0 ? reindexed[nextIndex].token : null];
  }));
}

function fromMaterial(material: UserMaterial, index: number): ExploreVideoComposerBoundImage {
  const token = buildToken(index);
  const previewUrl = getPreferredImageUrl(material.url || '', material.thumbnail_url);
  const mediaType = material.file_type === 'video' ? 'video' : 'image';
  return {
    token,
    source: 'upload',
    previewUrl,
    attachment: {
      nodeId: material.id || `uploaded-${mediaType}-${crypto.randomUUID()}`,
      assetId: material.id,
      mediaType,
      attachmentSource: 'asset',
      mediaUrl: material.url || '',
      thumbnail: previewUrl,
      label: token,
    },
  };
}

export const useExploreVideoComposerStore = create<ExploreVideoComposerState>((set, get) => ({
  ...initialState(),
  openEmpty: () => set(initialState()),
  setDraft: (draft) => set({ draft }),

  uploadReferenceImages: async (files, capabilitySlotKeys) => {
    const entries = files
      .map((file, index) => ({ file, capabilitySlotKey: capabilitySlotKeys?.[index] }))
      .filter(({ file }) => file.size > 0);
    if (entries.length === 0) return { ok: true };

    set({ isUploading: true, error: null });
    try {
      const uploaded = await Promise.all(entries.map(async ({ file }) => {
        const result = await materialsApi.uploadMaterial(file, 'general', {
          displayName: file.name || 'video-reference-image',
          assetCategory: 'project_asset',
        });
        if (result.error || !result.data?.url) {
          throw result.error ?? new Error(translateClient('explore', 'videoCreateUploadFailed'));
        }
        return result.data;
      }));
      const current = get();
      const nextImages = uploaded.map((material, index) => {
        const image = fromMaterial(material, current.boundImages.length + index + 1);
        const capabilitySlotKey = entries[index]?.capabilitySlotKey;
        return capabilitySlotKey
          ? { ...image, attachment: { ...image.attachment, capabilitySlotKey } }
          : image;
      });
      set({
        boundImages: reindex([...current.boundImages, ...nextImages]),
        isUploading: false,
        error: null,
      });
      return { ok: true };
    } catch (error) {
      const message = getUserFacingErrorMessage(
        error,
        'upload',
        translateClient('explore', 'videoCreateUploadFailed'),
      );
      set({ isUploading: false, error: message });
      return { ok: false, code: 'upload-failed', message };
    }
  },

  removeBoundImage: (token) => set(produce((state: ExploreVideoComposerState) => {
    const previous = [...state.boundImages];
    const remaining = previous.filter((image) => image.token.toLowerCase() !== token.toLowerCase());
    if (remaining.length === previous.length) return;
    const reindexed = reindex(remaining);
    state.draft = remapExploreImageReferenceTokens(
      state.draft,
      previous.map((image) => image.token),
      tokenRemap(previous, remaining, reindexed),
    );
    state.boundImages = reindexed;
  })),

  clearBoundImages: () => set(produce((state: ExploreVideoComposerState) => {
    const previous = [...state.boundImages];
    state.draft = remapExploreImageReferenceTokens(
      state.draft,
      previous.map((image) => image.token),
      Object.fromEntries(previous.map((image) => [image.token, null])),
    );
    state.boundImages = [];
  })),

  insertBoundImageReference: (token) => set(produce((state: ExploreVideoComposerState) => {
    const tokens = state.boundImages.map((image) => image.token);
    if (!tokens.some((candidate) => candidate.toLowerCase() === token.toLowerCase())) return;
    state.draft = appendExploreImageReferenceToken(state.draft, token.toLowerCase(), tokens);
    state.error = null;
  })),

  clearError: () => set({ error: null }),
  reset: () => set(initialState()),
}));
